/**
 * @author: Real Links <engineering@reallinks.io> (https://real-links.github.io)
 * @license: © 2020 Real Links Limited
 * @link: https://real-links.github.io
 */
import _get from 'lodash/get';
import _isPlainObject from 'lodash/isPlainObject';
import _set from 'lodash/set';

function assert(value, err) {
  if (!value) {
    throw err;
  }
}

function getData(store, key) {
  assert(key.startsWith('$'), new Error('Expected getData key to target $data or $value'));
  return _get(store, key.substr(1), null);
}

function setData(store, key, value) {
  assert(key.startsWith('$value.'), new Error('Expected setData key to target $value'));

  const before = JSON.stringify(_get(store, key.substr(1), null));
  const after = JSON.stringify(value);

  if (before !== after) {
    _set(store, key.substr(1), JSON.parse(after));
    this.$emit('input', JSON.parse(JSON.stringify(store.value)));
    this.$emit('update', { key: key.substr('$value.'.length), value: JSON.parse(after) });
    return true;
  } else {
    return false;
  }
}

export default {
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    layout: Object,
    value: Object,
  },
  render(createElement) {
    assert(_isPlainObject(this.layout), new Error('Expected layout prop to be an object'));
    assert(_isPlainObject(this.value), new Error('Expected value prop to be an object'));

    const data = {
      data: JSON.parse(JSON.stringify(this.data)),
      value: JSON.parse(JSON.stringify(this.value)),
    };

    const createBrick = el => {
      assert(el && el.is, new Error('Expected element to contain { is } property'));
      const { is, props, domProps, on, scopedSlots, children: childBricks, ...rest } = el;

      const context = {};
      let children = null;
      const name = typeof is === 'string' ? is : (typeof is === 'function' ? 'async-component' : (is.name || 'component'));

      if (_isPlainObject(props)) {
        context.props = {};
        context.on = context.on || {};
        for (const key in props) {
          if (props.hasOwnProperty(key)) {
            const { [key]: value } = props;
            if (key === 'v-model') {
              // Here, value is the v-model key
              assert(value.startsWith('$value.'), new Error(`Expected ${name}.props.v-model to target $value`));
              context.props.value = getData.call(this, data, value);
              context.on.input = input => setData.call(this, data, value, input);
            } else if (typeof value === 'function') {
              context.props[key] = value.call(context, data);
            } else if (typeof value === 'string' && (value.startsWith('$data.') || value.startsWith('$value.'))) {
              context.props[key] = getData.call(this, data, value);
            } else {
              context.props[key] = value;
            }
          }
        }
      }

      if (_isPlainObject(domProps)) {
        context.domProps = {};
        for (const key in domProps) {
          if (domProps.hasOwnProperty(key)) {
            const { [key]: value } = domProps;
            if (typeof value === 'function') {
              context.domProps[key] = value.call(context, data);
            } else if (typeof value === 'string' && (value.startsWith('$data.') || value.startsWith('$value.'))) {
              context.domProps[key] = getData.call(this, data, value);
            } else {
              context.domProps[key] = value;
            }
          }
        }
      }

      if (_isPlainObject(on)) {
        context.on = context.on || {};
        for (const key in on) {
          if (on.hasOwnProperty(key)) {
            const { [key]: value } = on;
            if (typeof value === 'function') {
              context.on[key] = (...args) => value({ getData: getData.bind(this, data), setData: setData.bind(this, data) }, ...args);
            } else if (typeof value === 'string' && (value.startsWith('$data.') || value.startsWith('$value.'))) {
              context.on[key] = input => setData.call(this, data, value, input);
            } else {
              throw new Error(`Expected ${name}.on[${value}] to be a function`);
            }
          }
        }
      }

      if (_isPlainObject(scopedSlots)) {
        context.scopedSlots = {};
        for (const key in scopedSlots) {
          if (scopedSlots.hasOwnProperty(key)) {
            context.scopedSlots[key] = props => scopedSlots[key].call(context, createElement, props);
          }
        }
      }

      if (childBricks) {
        if (childBricks.is) {
          children = [ createBrick(childBricks) ];
        } else if (Array.isArray(childBricks)) {
          children = childBricks.filter(({ is }) => is).map(createBrick);
        } else if (_isPlainObject(childBricks)) {
          children = Object.values(childBricks).filter(({ is }) => is).map(createBrick);
        } else {
          children = childBricks;
        }
      }

      return createElement(is, { ...context, ...rest }, children);
    };

    return createBrick(this.layout);
  },
};



// WEBPACK FOOTER //
// ./src/plugins/vue-form-bricks/index.js